import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { Avatar, Button } from 'components/Elements'
import { FILE_MAX_SIZE } from 'consts/core'
import { useNotify } from 'hooks'
import { FileUtils, ValidationsUtils } from 'utils'
import s from './AvatarManagerControl.module.scss'

export const AvatarManagerControl = ({
  image,
  onUpload,
  onRemove,
  uploading = false,
  removing = false,
  avatarVariant,
  uploadLabel,
  deleteLabel,
}) => {
  const { t } = useTranslation()

  const { showNotification } = useNotify()

  const [selectedPhoto, setSelectedPhoto] = useState(null)

  const inputRef = useRef(null)

  const selectPhoto = event => {
    event.preventDefault()

    if (inputRef.current) {
      inputRef.current.click()
    }
  }

  useEffect(() => {
    if (image) {
      setSelectedPhoto(null)
    }
  }, [image])

  const onInputChange = event => {
    if (!event.target.files) return

    const file = event.target.files[0]

    const isValid = ValidationsUtils.fileMaxSize(file, FILE_MAX_SIZE)

    if (!isValid) {
      const message = t('file.max_size.error', {
        text: `${FileUtils.formatBytes(FILE_MAX_SIZE)}`,
      })
      showNotification({ type: 'error', message: message })

      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      if (reader.readyState === 2) {
        setSelectedPhoto(reader.result)
      }
    }
    reader.readAsDataURL(file)

    onUpload(file)

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  uploadLabel = uploadLabel || t('upload_photo')
  deleteLabel = deleteLabel || t('delete_photo')

  return (
    <>
      <div className={s.block}>
        <div>
          <Avatar
            size={'lg'}
            src={selectedPhoto ? selectedPhoto : image ? image : undefined}
            variant={avatarVariant}
          />
        </div>

        <input
          type="file"
          onChange={onInputChange}
          ref={inputRef}
          accept="image/png, image/jpeg"
          className={'d-none'}
        />

        <div className={s.controlsBlock}>
          <Button
            onClick={selectPhoto}
            size={'sm'}
            disabled={uploading || removing}
            loading={uploading}
          >
            {uploadLabel}
          </Button>

          {image && (
            <Button
              outline
              color={'danger'}
              onClick={onRemove}
              size={'sm'}
              disabled={uploading || removing}
              loading={removing}
            >
              {deleteLabel}
            </Button>
          )}
        </div>
      </div>
    </>
  )
}

AvatarManagerControl.propTypes = {
  image: PropTypes.string,
  onUpload: PropTypes.func,
  onRemove: PropTypes.func,
  uploading: PropTypes.bool,
  removing: PropTypes.bool,
  avatarVariant: PropTypes.string,
  uploadLabel: PropTypes.string,
  deleteLabel: PropTypes.string,
}
